import { supabase } from './supabase';
import { Database } from './database.types';
import * as Sentry from '@sentry/react';

type SubscriptionPlan = Database['public']['Tables']['subscription_plans']['Row'];
type UserSubscription = Database['public']['Tables']['user_subscriptions']['Row'];
type SubscriptionLimits = Database['public']['Tables']['subscription_limits']['Row'];

export interface CreateSubscriptionParams {
  userId: string;
  planId: string;
  stripeSubscriptionId?: string | any; // Allow string or Stripe subscription object
  stripeCustomerId?: string;
  currentPeriodStart?: Date;
  currentPeriodEnd?: Date;
}

export interface SubscriptionUsage {
  analysesUsed: number;
  teamSeatsUsed: number;
  projectsCount: number;
  versionsUsed: number;
  analysesLimit: number;
  teamSeatsLimit: number;
  projectsLimit: number;
  versionsLimit: number;
}

export class SubscriptionService {
  /**
   * Map database SubscriptionPlan to frontend Plan format
   */
  private static mapDbPlanToFrontend(dbPlan: SubscriptionPlan): any {
    return {
      id: dbPlan.id,
      name: dbPlan.name,
      priceMonthly: dbPlan.price_monthly, // Price in cents
      monthlyCredits: dbPlan.monthly_credits,
      features: dbPlan.features || [],
      popular: dbPlan.is_popular,
      teamSeats: dbPlan.team_seats_limit === -1 ? 'Unlimited' : dbPlan.team_seats_limit,
      projects: dbPlan.projects_limit === -1 ? 'Unlimited' : dbPlan.projects_limit,
      versions: dbPlan.versions_limit === -1 ? 'Unlimited' : dbPlan.versions_limit
    };
  }

  /**
   * Get all available subscription plans in frontend format
   */
  static async getAvailablePlans(): Promise<any[]> {
    const { data, error } = await supabase
      .from('subscription_plans')
      .select('*')
      .order('price_monthly');

    if (error) {
      console.error('Error fetching subscription plans:', error);
      throw new Error(`Failed to fetch subscription plans: ${error.message}`);
    }

    // Map database format to frontend format
    return (data || []).map(plan => this.mapDbPlanToFrontend(plan));
  }

  /**
   * Get subscription plan details by ID
   */
  static async getPlanDetails(planId: string): Promise<SubscriptionPlan | null> {
    const { data, error } = await supabase
      .from('subscription_plans')
      .select('*')
      .eq('id', planId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null; // Not found
      console.error('Error fetching plan details:', error);
      throw new Error(`Failed to fetch plan details: ${error.message}`);
    }

    return data;
  }

  /**
   * Get user's current subscription (including canceled ones within billing period)
   */
  static async getUserSubscription(userId: string): Promise<UserSubscription | null> {
    
    // Use service role to bypass RLS when looking up organization owner subscriptions
    const { data: { user: currentUser } } = await supabase.auth.getUser();
    const isLookingUpOtherUser = currentUser?.id !== userId;
    
    if (isLookingUpOtherUser) {
    }
    
    const query = supabase
      .from('user_subscriptions')
      .select('*')
      .eq('user_id', userId)
      .in('status', ['active', 'canceled'])
      .order('created_at', { ascending: false })
      .limit(1);

    // If looking up another user's subscription (like org owner), we need to use RPC or service role
    // For now, let's try the RPC approach
    let subscriptions;
    let error;
    
    if (isLookingUpOtherUser) {
      const rpcResult = await supabase.rpc('get_user_subscription_admin', { target_user_id: userId });
      subscriptions = rpcResult.data;
      error = rpcResult.error;
    } else {
      const queryResult = await query;
      subscriptions = queryResult.data;
      error = queryResult.error;
    }

    if (error) {
      console.error('Error fetching user subscription:', error);
      throw new Error(`Failed to fetch user subscription: ${error.message}`);
    }


    if (!subscriptions || subscriptions.length === 0) {
      // Debug: Let's see what subscriptions exist for this user (any status) - but only if it's own user due to RLS
      if (!isLookingUpOtherUser) {
        const { data: allSubs } = await supabase
          .from('user_subscriptions')
          .select('*')
          .eq('user_id', userId);
      } else {
      }
      return null;
    }

    const subscription = subscriptions[0];
    
    // Check subscription validity based on billing period
    const now = new Date();
    const periodEnd = subscription.current_period_end ? new Date(subscription.current_period_end) : null;
    
    
    // For canceled subscriptions, check if they're still within the billing period
    if (subscription.status === 'canceled') {
      // If subscription is canceled but still within period, return it
      // Otherwise, treat as if no subscription exists
      if (!periodEnd || now >= periodEnd) {
        return null;
      }
    }
    
    // Special case: If period_start equals period_end, this is a webhook bug
    // All recent subscriptions have this issue - treat active status as valid
    const periodStart = subscription.current_period_start ? new Date(subscription.current_period_start) : null;
    if (subscription.status === 'active' && periodStart && periodEnd && 
        periodStart.getTime() === periodEnd.getTime()) {
      // Return the subscription as valid since it's active in Stripe
      return subscription;
    }
    
    // For active subscriptions that have expired, we still return them 
    // as they might have grace period or need to be handled by billing logic
    if (subscription.status === 'active' && periodEnd && now >= periodEnd) {
      console.log('⚠️ Active subscription has expired but returning it for grace period handling');
      // Don't return null here - let the business logic handle expired active subscriptions
    }

    return subscription;
  }

  /**
   * Create a new subscription for a user
   */
  static async createSubscription(params: CreateSubscriptionParams): Promise<UserSubscription> {
    const { userId, planId, stripeCustomerId, currentPeriodStart, currentPeriodEnd } = params;
    
    // Ensure stripeSubscriptionId is a string, not an object
    let stripeSubscriptionId: string | undefined;
    if (typeof params.stripeSubscriptionId === 'string') {
      stripeSubscriptionId = params.stripeSubscriptionId;
    } else if (params.stripeSubscriptionId && typeof params.stripeSubscriptionId === 'object') {
      stripeSubscriptionId = params.stripeSubscriptionId.id;
    }

    // Verify the plan exists
    const plan = await this.getPlanDetails(planId);
    if (!plan) {
      throw new Error(`Subscription plan '${planId}' not found`);
    }

    // Check if user already has an active subscription
    const { data: existingUserSub } = await supabase
      .from('user_subscriptions')
      .select('*')
      .eq('user_id', userId)
      .eq('status', 'active')
      .maybeSingle();

    if (existingUserSub) {
      console.log('✅ User already has an active subscription:', existingUserSub.id);
      
      // If the existing subscription is for the same plan, return it
      if (existingUserSub.plan_id === planId) {
        return existingUserSub;
      }
      
      // Otherwise, update the existing subscription
      const { data: updatedSub, error: updateError } = await supabase
        .from('user_subscriptions')
        .update({
          plan_id: planId,
          stripe_subscription_id: stripeSubscriptionId,
          stripe_customer_id: stripeCustomerId,
          current_period_start: currentPeriodStart?.toISOString(),
          current_period_end: currentPeriodEnd?.toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', existingUserSub.id)
        .select()
        .single();

      if (updateError) {
        console.error('Error updating subscription:', updateError);
        throw new Error(`Failed to update subscription: ${updateError.message}`);
      }

      return updatedSub;
    }

    // If we have a stripe subscription ID, check if it already exists
    if (stripeSubscriptionId) {
      const { data: existingStripeSub } = await supabase
        .from('user_subscriptions')
        .select('*')
        .eq('stripe_subscription_id', stripeSubscriptionId)
        .maybeSingle();

      if (existingStripeSub) {
        console.log('✅ Stripe subscription already exists:', existingStripeSub.id);
        return existingStripeSub;
      }
    }

    // Create new subscription
    const { data, error } = await supabase
      .from('user_subscriptions')
      .insert({
        user_id: userId,
        plan_id: planId,
        stripe_subscription_id: stripeSubscriptionId,
        stripe_customer_id: stripeCustomerId,
        current_period_start: currentPeriodStart?.toISOString(),
        current_period_end: currentPeriodEnd?.toISOString(),
        status: 'active'
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating subscription:', error);
      throw new Error(`Failed to create subscription: ${error.message}`);
    }

    // Initialize subscription limits for the user
    await this.initializeSubscriptionLimits(userId);

    // Update user's subscription_plan field
    const { error: userUpdateError } = await supabase
      .from('users')
      .update({ subscription_plan: planId })
      .eq('id', userId);

    if (userUpdateError) {
      console.warn('Warning: Failed to update user subscription_plan field:', userUpdateError);
      // Don't throw error as the subscription is already created
    }

    // Note: GTM purchase tracking is handled by PaymentSuccessPage when user returns from Stripe
    console.log('✅ Subscription created successfully:', plan?.name);

    return data;
  }

  /**
   * Initialize subscription limits for a user
   */
  static async initializeSubscriptionLimits(userId: string): Promise<void> {
    console.log('🔄 Initializing/resetting subscription limits for user:', userId);
    
    const { error } = await supabase
      .from('subscription_limits')
      .upsert({
        user_id: userId,
        current_analyses_used: 0,
        current_team_seats_used: 0,
        current_projects_count: 0,
        current_versions_used: 0,
        current_ad_creative_used: 0,
        current_period_start: new Date().toISOString(),
        ad_creative_period_start: new Date().toISOString(),
        updated_at: new Date().toISOString()
      });

    if (error) {
      console.error('Error initializing subscription limits:', error);
      throw new Error(`Failed to initialize subscription limits: ${error.message}`);
    }
    
    console.log('✅ Subscription limits initialized/reset successfully for user:', userId);
  }

  /**
   * Get user's current usage and limits (team-aware)
   */
  static async getUserUsage(userId: string): Promise<SubscriptionUsage> {
    // Check if user is part of an organization (team member)
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('organization_id, team_role, subscription_plan')
      .eq('id', userId)
      .single();

    if (userError) {
      console.error('Error fetching user data:', userError);
      throw new Error(`Failed to fetch user data: ${userError.message}`);
    }

    // If user is a team member, use organization owner's subscription and shared limits
    if (userData.organization_id && userData.team_role && userData.subscription_plan === null) {
      console.log('👥 Team member detected, using organization shared credits');
      
      // Find organization owner using created_by from organizations table
      console.log('🔍 Looking for organization owner for org:', userData.organization_id);
      
      const { data: orgData, error: orgError } = await supabase
        .from('organizations')
        .select('created_by')
        .eq('id', userData.organization_id)
        .single();
      
      if (orgError || !orgData?.created_by) {
        throw new Error('Organization owner not found');
      }
      
      const { data: orgOwner, error: ownerError } = await supabase
        .from('users')
        .select('id, email, subscription_plan, team_role')
        .eq('id', orgData.created_by)
        .single();

      console.log('👤 Organization owner query result:', { orgOwner, ownerError });

      if (ownerError || !orgOwner) {
        // Debug: Let's see what users exist in this organization
        const { data: allOrgUsers } = await supabase
          .from('users')
          .select('id, email, subscription_plan, team_role')
          .eq('organization_id', userData.organization_id);
        console.log('🔍 All users in organization:', allOrgUsers);
        throw new Error('Organization owner with subscription not found');
      }

      // Get owner's subscription and plan
      const subscription = await this.getUserSubscription(orgOwner.id);
      if (!subscription) {
        throw new Error('Organization does not have an active subscription');
      }

      const plan = await this.getPlanDetails(subscription.plan_id);
      if (!plan) {
        throw new Error('Organization subscription plan not found');
      }

      // Get organization owner's usage (single source of truth for all team members)
      const { data: ownerLimitsArray, error: limitsError } = await supabase
        .rpc('get_team_aware_subscription_limits', { target_user_id: orgOwner.id });

      const ownerLimits = ownerLimitsArray?.[0] || null;

      if (limitsError) {
        console.error('Error fetching organization owner limits:', limitsError);
        throw new Error(`Failed to fetch organization owner limits: ${limitsError.message}`);
      }

      // Count organization members for team seats
      const { data: orgMembers } = await supabase
        .from('users')
        .select('id')
        .eq('organization_id', userData.organization_id);

      const totalAnalysesUsed = ownerLimits?.current_analyses_used || 0;
      const totalTeamSeatsUsed = orgMembers?.length || 1;
      const totalProjectsCount = ownerLimits?.current_projects_count || 0;
      const totalVersionsUsed = ownerLimits?.current_versions_used || 0;
      const purchasedCredits = ownerLimits?.purchased_analysis_credits || 0;
      

      console.log('📊 Organization usage:', {
        totalAnalysesUsed,
        totalTeamSeatsUsed,
        totalProjectsCount,
        totalVersionsUsed,
        planLimits: {
          analysesLimit: plan.monthly_credits,
          teamSeatsLimit: plan.team_seats_limit,
          projectsLimit: plan.projects_limit,
          versionsLimit: plan.versions_limit
        }
      });

      return {
        analysesUsed: totalAnalysesUsed,
        teamSeatsUsed: totalTeamSeatsUsed,
        projectsCount: totalProjectsCount,
        versionsUsed: totalVersionsUsed,
        analysesLimit: (plan.monthly_credits || 0) + purchasedCredits, // Include purchased credits
        teamSeatsLimit: plan.team_seats_limit || 1,
        projectsLimit: plan.projects_limit || 1,
        versionsLimit: plan.versions_limit || 1,
      };
    }

    // Individual user - check for active subscription or use trial plan
    const subscription = await this.getUserSubscription(userId);
    let plan: any;
    
    if (!subscription) {
      // No active subscription - check if user has a trial plan
      if (userData.subscription_plan === 'trial') {
        plan = await this.getPlanDetails('trial');
        if (!plan) {
          throw new Error('Trial plan not found');
        }
      } else {
        throw new Error('User does not have an active subscription');
      }
    } else {
      plan = await this.getPlanDetails(subscription.plan_id);
      if (!plan) {
        throw new Error('Subscription plan not found');
      }
    }

    // Use team-aware function to get subscription limits
    const { data: limitsArray, error } = await supabase
      .rpc('get_team_aware_subscription_limits', { target_user_id: userId });

    const limits = limitsArray?.[0] || null;

    if (error) {
      console.error('Error fetching subscription limits:', error);
      throw new Error(`Failed to fetch subscription limits: ${error.message}`);
    }

    if (!limits) {
      throw new Error('No subscription limits found');
    }

    // Include purchased credits in the analysis limit
    const purchasedCredits = limits.purchased_analysis_credits || 0;
    
    return {
      analysesUsed: limits.current_analyses_used || 0,
      teamSeatsUsed: limits.current_team_seats_used || 0,
      projectsCount: limits.current_projects_count || 0,
      versionsUsed: limits.current_versions_used || 0,
      analysesLimit: (plan.monthly_credits || 0) + purchasedCredits,
      teamSeatsLimit: plan.team_seats_limit || 1,
      projectsLimit: plan.projects_limit || 1,
      versionsLimit: plan.versions_limit || 1,
    };
  }

  /**
   * Update user's subscription to a new plan
   */
  static async upgradeSubscription(
    userId: string, 
    newPlanId: string, 
    stripeSubscriptionId?: string
  ): Promise<UserSubscription> {
    // Verify the new plan exists
    const newPlan = await this.getPlanDetails(newPlanId);
    if (!newPlan) {
      throw new Error(`Subscription plan '${newPlanId}' not found`);
    }

    // Get current subscription and plan details
    const currentSubscription = await this.getUserSubscription(userId);
    if (!currentSubscription) {
      throw new Error('No active subscription found');
    }

    const currentPlan = await this.getPlanDetails(currentSubscription.plan_id);
    if (!currentPlan) {
      throw new Error('Current subscription plan not found');
    }

    // Note: No credit preservation needed - users get fresh credits for new plan

    // Update the subscription
    const { data, error } = await supabase
      .from('user_subscriptions')
      .update({
        plan_id: newPlanId,
        stripe_subscription_id: stripeSubscriptionId,
        updated_at: new Date().toISOString()
      })
      .eq('user_id', userId)
      .eq('status', 'active')
      .select()
      .single();

    if (error) {
      console.error('Error upgrading subscription:', error);
      throw new Error(`Failed to upgrade subscription: ${error.message}`);
    }

    // Note: GTM purchase tracking for upgrades is handled by PaymentSuccessPage when user returns from Stripe
    console.log('✅ Subscription upgraded successfully:', newPlan?.name);

    return data;
  }

  /**
   * Cancel user's subscription
   */
  static async cancelSubscription(userId: string, immediate: boolean = false): Promise<void> {
    const updateData: any = {
      updated_at: new Date().toISOString()
    };

    if (immediate) {
      updateData.status = 'canceled';
    } else {
      updateData.cancel_at_period_end = true;
    }

    const { error } = await supabase
      .from('user_subscriptions')
      .update(updateData)
      .eq('user_id', userId)
      .eq('status', 'active');

    if (error) {
      console.error('Error canceling subscription:', error);
      throw new Error(`Failed to cancel subscription: ${error.message}`);
    }
  }

  /**
   * Calculate upgrade proration cost
   */
  static async calculateUpgradeProration(
    currentPlanId: string, 
    newPlanId: string, 
    currentPeriodEnd: Date
  ): Promise<number> {
    const currentPlan = await this.getPlanDetails(currentPlanId);
    const newPlan = await this.getPlanDetails(newPlanId);

    if (!currentPlan || !newPlan) {
      throw new Error('Plan not found for proration calculation');
    }

    const now = new Date();
    const totalPeriodDays = 30; // Assuming monthly billing
    const remainingDays = Math.max(0, Math.ceil((currentPeriodEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
    
    const currentPlanDailyRate = currentPlan.price_monthly / totalPeriodDays;
    const newPlanDailyRate = newPlan.price_monthly / totalPeriodDays;
    
    const prorationAmount = (newPlanDailyRate - currentPlanDailyRate) * remainingDays;
    
    return Math.max(0, Math.round(prorationAmount)); // Return in cents
  }

  /**
   * Validate if user can change to a new plan
   */
  static async validatePlanChange(userId: string, newPlanId: string): Promise<boolean> {
    const currentSubscription = await this.getUserSubscription(userId);
    const newPlan = await this.getPlanDetails(newPlanId);
    
    if (!currentSubscription || !newPlan) {
      return false;
    }

    // Add any business logic for plan change validation
    // For now, allow all plan changes
    return true;
  }

  /**
   * Reset monthly usage for billing cycle
   */
  static async resetMonthlyUsage(userId: string): Promise<void> {
    const { error } = await supabase
      .from('subscription_limits')
      .update({
        current_analyses_used: 0,
        current_period_start: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('user_id', userId);

    if (error) {
      console.error('Error resetting monthly usage:', error);
      throw new Error(`Failed to reset monthly usage: ${error.message}`);
    }
  }




  /**
   * Check and complete pending subscription after payment
   */
  static async checkAndCompletePendingSubscription(userId: string): Promise<{success: boolean, message: string, planId?: string}> {
    try {
      // Check if there's a subscription intent stored
      if (typeof window === 'undefined') {
        return { success: false, message: 'No browser environment' };
      }

      const intentData = localStorage.getItem('rulevia_subscription_intent');
      if (!intentData) {
        return { success: false, message: 'No pending subscription found' };
      }

      const intent = JSON.parse(intentData);
      
      // Check if intent is for current user and not too old (24 hours)
      if (intent.userId !== userId || Date.now() - intent.timestamp > 24 * 60 * 60 * 1000) {
        localStorage.removeItem('rulevia_subscription_intent');
        localStorage.removeItem('rulevia_payment_message');
        return { success: false, message: 'Subscription intent expired or invalid' };
      }

      // Check if user already has this subscription
      const existingSubscription = await this.getUserSubscription(userId);
      if (existingSubscription && existingSubscription.plan_id === intent.planId) {
        // Subscription was created by webhook - no need to track purchase here
        // GTM tracking is handled by PaymentSuccessPage when user returns from Stripe
        console.log('✅ Subscription already exists - created by webhook for plan:', intent.planId);
        
        // Clear the intent - subscription already exists
        localStorage.removeItem('rulevia_subscription_intent');
        localStorage.removeItem('rulevia_payment_message');
        return { success: true, message: 'Subscription already active', planId: intent.planId };
      }

      // For now, we'll create the subscription manually since we can't verify payment
      // In a real implementation, this would verify the payment with Stripe first
      // Note: Purchase tracking is handled automatically in createSubscription method
      const newSubscription = await this.createSubscription({
        userId: intent.userId,
        planId: intent.planId,
        currentPeriodStart: new Date(),
        currentPeriodEnd: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) // 30 days
      });

      // Clear the intent
      localStorage.removeItem('rulevia_subscription_intent');
      localStorage.removeItem('rulevia_payment_message');

      return { 
        success: true, 
        message: `${intent.planId} subscription activated successfully!`,
        planId: intent.planId 
      };

    } catch (error) {
      console.error('Error completing pending subscription:', error);
      return { 
        success: false, 
        message: `Failed to complete subscription: ${error instanceof Error ? error.message : 'Unknown error'}` 
      };
    }
  }

  /**
   * Get pending subscription message for user
   */
  static getPendingSubscriptionMessage(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('rulevia_payment_message');
  }

  /**
   * Clear pending subscription data
   */
  static clearPendingSubscriptionData(): void {
    if (typeof window === 'undefined') return;
    localStorage.removeItem('rulevia_subscription_intent');
    localStorage.removeItem('rulevia_payment_message');
    localStorage.removeItem('rulevia_payment_instructions');
    localStorage.removeItem('rulevia_payment_session_id');
  }

  /**
   * Create checkout session for credit pack purchase
   */
  static async createCreditPackCheckout(
    userId: string,
    packageId: string,
    successUrl: string,
    cancelUrl: string
  ): Promise<string> {
    try {
      // Get credit package details
      const { data: creditPackage, error } = await supabase
        .from('credit_packages')
        .select('*')
        .eq('id', packageId)
        .single();

      if (error || !creditPackage || !creditPackage.stripe_price_id) {
        throw new Error(`Credit package '${packageId}' not found or missing Stripe price ID`);
      }

      // For now, return test URLs (we'll enhance this with proper checkout sessions later)
      const testUrls = {
        'Mini Pack': 'https://buy.stripe.com/test_fZu14pecyeGebfb2MK2ZO01',
        'Standard Pack': 'https://buy.stripe.com/test_fZu14pecyeGebfb2MK2ZO01',
        'Plus Pack': 'https://buy.stripe.com/test_fZu14pecyeGebfb2MK2ZO01',
        'Maxi Pack': 'https://buy.stripe.com/test_fZu14pecyeGebfb2MK2ZO01'
      };

      return testUrls[creditPackage.name as keyof typeof testUrls] || cancelUrl;
    } catch (error) {
      console.error('Error creating credit pack checkout:', error);
      throw new Error(`Failed to create credit pack checkout: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Handle successful subscription payment from Stripe webhook
   */
  static async handleSubscriptionSuccess(
    userId: string,
    planId: string,
    stripeSubscriptionId: string,
    stripeCustomerId: string,
    currentPeriodStart: string,
    currentPeriodEnd: string
  ): Promise<void> {
    try {
      // Create or update subscription
      const { error } = await supabase
        .from('user_subscriptions')
        .upsert({
          user_id: userId,
          plan_id: planId,
          stripe_subscription_id: stripeSubscriptionId,
          stripe_customer_id: stripeCustomerId,
          current_period_start: currentPeriodStart,
          current_period_end: currentPeriodEnd,
          status: 'active'
        });

      if (error) {
        throw error;
      }

      // Initialize or reset subscription limits
      await this.initializeSubscriptionLimits(userId);
    } catch (error) {
      console.error('Error handling subscription success:', error);
      throw new Error(`Failed to handle subscription success: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Handle failed subscription payment from Stripe webhook
   */
  static async handleSubscriptionFailure(userId: string, stripeSubscriptionId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('user_subscriptions')
        .update({
          status: 'past_due',
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId)
        .eq('stripe_subscription_id', stripeSubscriptionId);

      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Error handling subscription failure:', error);
      throw new Error(`Failed to handle subscription failure: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get available credit packages
   */
  static async getAvailableCreditPackages() {
    const { data, error } = await supabase
      .from('credit_packages')
      .select('*')
      .eq('is_active', true)
      .order('credits');

    if (error) {
      console.error('Error fetching credit packages:', error);
      throw new Error(`Failed to fetch credit packages: ${error.message}`);
    }

    return data || [];
  }



  /**
   * Check if a plan change is an upgrade or downgrade
   */
  static async isPlanUpgrade(currentPlanId: string, newPlanId: string): Promise<boolean> {
    try {
      const { data: plans, error } = await supabase
        .from('subscription_plans')
        .select('id, price_monthly')
        .in('id', [currentPlanId, newPlanId]);

      if (error || !plans || plans.length !== 2) {
        throw new Error('Failed to fetch plan details');
      }

      const currentPlan = plans.find(p => p.id === currentPlanId);
      const newPlan = plans.find(p => p.id === newPlanId);

      if (!currentPlan || !newPlan) {
        throw new Error('Plan not found');
      }

      return newPlan.price_monthly > currentPlan.price_monthly;
    } catch (error) {
      console.error('Error checking plan upgrade:', error);
      // Default to treating as upgrade for safety
      return true;
    }
  }

  /**
   * Preview subscription change (upgrade/downgrade) - All changes are immediate
   */
  static async previewSubscriptionChange(
    userId: string, 
    newPlanId: string
  ): Promise<{
    isUpgrade: boolean;
    proratedAmount?: number;
    effectiveDate: Date;
    currentPlan: any;
    newPlan: any;
  } | null> {
    try {
      // Get current subscription
      const currentSubscription = await this.getUserSubscription(userId);
      if (!currentSubscription) {
        throw new Error('No active subscription found');
      }

      // Get plan details
      const [currentPlan, newPlan] = await Promise.all([
        this.getPlanDetails(currentSubscription.plan_id),
        this.getPlanDetails(newPlanId)
      ]);

      if (!currentPlan || !newPlan) {
        throw new Error('Plan details not found');
      }

      const isUpgrade = newPlan.price_monthly > currentPlan.price_monthly;
      // All plan changes are now immediate
      const effectiveDate = new Date();

      // For upgrades, calculate proration (Stripe handles this automatically)
      let proratedAmount = 0;
      if (isUpgrade && currentSubscription.current_period_end) {
        const now = new Date();
        const periodEnd = new Date(currentSubscription.current_period_end);
        const totalPeriodDays = 30; // Assuming monthly billing
        const remainingDays = Math.max(0, Math.ceil((periodEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
        
        const currentDailyRate = currentPlan.price_monthly / totalPeriodDays;
        const newDailyRate = newPlan.price_monthly / totalPeriodDays;
        
        proratedAmount = Math.round((newDailyRate - currentDailyRate) * remainingDays);
      }

      return {
        isUpgrade,
        proratedAmount: isUpgrade ? proratedAmount : undefined,
        effectiveDate,
        currentPlan: this.mapDbPlanToFrontend(currentPlan),
        newPlan: this.mapDbPlanToFrontend(newPlan)
      };

    } catch (error) {
      console.error('Error previewing subscription change:', error);
      return null;
    }
  }
} 